// Centro de Notificaciones: lista agrupada por fecha, pestañas por estado de
// lectura y filtros desplegables (módulo, estado, tipo y ciudad).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, DatePicker, Empty, Modal, Result, Skeleton, Typography, message, theme } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import {
  actionNotificacionesGet,
  actionNotificacionesMarcarLeidas,
} from "../../redux/actions/notificaciones/notificaciones";
import { actionUsuariosGet } from "../../redux/actions/usuarios/usuarios";
import { useEsAdministracion } from "../../context/PermisosContext";
import useSocket from "../../hooks/useSocket";
import FiltroChip from "../../components/notificaciones/FiltroChip";
import { NotificacionRafaga, NotificacionSimple } from "../../components/notificaciones/NotificacionItem";
import {
  CATEGORIAS,
  CITY_NAMES,
  DEPARTAMENTOS,
  ESTADOS,
  PAGE_SIZE,
  RANGOS_FECHA,
} from "../../components/notificaciones/notificacionesConfig";
import {
  agruparPorFecha,
  agruparRafagas,
  construirItems,
  dentroDeRangoFecha,
} from "../../components/notificaciones/notificacionesUtils";
import "../../components/notificaciones/notificaciones.css";

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;

const TODOS_DEPARTAMENTOS = Object.keys(DEPARTAMENTOS);
const TODOS_ESTADOS = Object.keys(ESTADOS);
const TODAS_CATEGORIAS = Object.keys(CATEGORIAS);
const TODAS_CIUDADES = Object.keys(CITY_NAMES);

const TAB_FILTROS = {
  todas: () => true,
  no_leidas: (i) => !i.leido,
  leidas: (i) => i.leido,
  urgentes: (i) => i.urgente,
};

export default function Notificaciones() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = theme.useToken();

  const notiSlice = useSelector((state) => state.notificaciones || {});
  const apiItems = useMemo(() => notiSlice?.data?.items || [], [notiSlice]);
  const userId = notiSlice?.data?.user_id;
  const loading = Boolean(notiSlice?.loading);
  const errorMsg = notiSlice?.error?.error || null;

  const esAdministracion = useEsAdministracion();
  const usuariosData = useSelector((state) => state.usuarios?.data);
  const listaUsuarios = useMemo(
    () => (Array.isArray(usuariosData?.items) ? usuariosData.items : []),
    [usuariosData]
  );
  const TODOS_USUARIOS = useMemo(() => listaUsuarios.map((u) => String(u.id_user)), [listaUsuarios]);

  const [tab, setTab] = useState("todas");
  const [departamentos, setDepartamentos] = useState(TODOS_DEPARTAMENTOS);
  const [estados, setEstados] = useState(TODOS_ESTADOS);
  const [tipos, setTipos] = useState(TODAS_CATEGORIAS);
  const [ciudades, setCiudades] = useState(TODAS_CIUDADES);
  const [usuariosFiltro, setUsuariosFiltro] = useState(null);
  const [rangoFecha, setRangoFecha] = useState("todas");
  const [rangoPersonalizado, setRangoPersonalizado] = useState([null, null]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Carga la lista de usuarios solo para administracion (unico rol que ve el filtro).
  useEffect(() => {
    if (esAdministracion) dispatch(actionUsuariosGet());
  }, [esAdministracion, dispatch]);

  // Al llegar la lista de usuarios, selecciona "Todos" por defecto.
  useEffect(() => {
    if (esAdministracion && usuariosFiltro === null && TODOS_USUARIOS.length > 0) {
      setUsuariosFiltro(TODOS_USUARIOS);
    }
  }, [esAdministracion, usuariosFiltro, TODOS_USUARIOS]);

  const items = useMemo(() => construirItems(apiItems, userId), [apiItems, userId]);

  const counts = useMemo(
    () => ({
      todas: items.length,
      no_leidas: items.filter((i) => !i.leido).length,
      leidas: items.filter((i) => i.leido).length,
      urgentes: items.filter((i) => i.urgente).length,
    }),
    [items]
  );

  const filtrados = useMemo(
    () =>
      items.filter((i) => {
        if (!TAB_FILTROS[tab](i)) return false;
        if (!departamentos.includes(i.departamento)) return false;
        if (!estados.includes(i.estado.clave)) return false;
        if (!tipos.includes(i.categoria)) return false;
        if (i.ciudadId != null && CITY_NAMES[i.ciudadId] && !ciudades.includes(String(i.ciudadId))) return false;
        if (esAdministracion && usuariosFiltro != null && i.idUsuario != null) {
          if (!usuariosFiltro.includes(String(i.idUsuario))) return false;
        }
        if (!dentroDeRangoFecha(i.fecha, rangoFecha, rangoPersonalizado)) return false;
        return true;
      }),
    [items, tab, departamentos, estados, tipos, ciudades, esAdministracion, usuariosFiltro, rangoFecha, rangoPersonalizado]
  );

  const entradas = useMemo(() => agruparRafagas(filtrados), [filtrados]);
  const visibles = entradas.slice(0, visibleCount);
  const grupos = useMemo(() => agruparPorFecha(visibles), [visibles]);
  const notificacionesMostradas = visibles.reduce((n, e) => n + e.items.length, 0);

  const noLeidasFiltradas = filtrados.filter((i) => !i.leido);

  // Al cambiar pestaña o filtros, volver a la primera página.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tab, departamentos, estados, tipos, ciudades, usuariosFiltro, rangoFecha, rangoPersonalizado]);

  useEffect(() => {
    dispatch(actionNotificacionesGet());
  }, [dispatch]);

  // Refresco en tiempo real: si llega una notificación mientras estás aquí.
  const handleSocketEvent = useCallback(
    (msg) => {
      if (msg?.type === "NOTIFICACION_INVALIDATE") {
        dispatch(actionNotificacionesGet());
      }
    },
    [dispatch]
  );
  useSocket("event", handleSocketEvent);

  const abrirNotificacion = (item) => {
    if (!item.ruta) return;
    if (!item.leido) {
      dispatch(actionNotificacionesMarcarLeidas([item.db_id])).catch(() => {});
    }
    navigate(item.ruta);
  };

  const expandirRafaga = (items) => {
    const ids = items.filter((i) => !i.leido).map((i) => i.db_id);
    if (ids.length > 0) {
      dispatch(actionNotificacionesMarcarLeidas(ids)).catch(() => {});
    }
  };

  const confirmarMarcarTodas = () => {
    const ids = noLeidasFiltradas.map((i) => i.db_id);
    Modal.confirm({
      title: "Marcar notificaciones como leídas",
      content: `Se marcarán como leídas ${ids.length} notificaciones que coinciden con los filtros activos.`,
      okText: "Marcar como leídas",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionNotificacionesMarcarLeidas(ids));
          message.success(`${ids.length} notificaciones marcadas como leídas`);
        } catch {
          message.error("No se pudieron marcar como leídas");
        }
      },
    });
  };

  const quitarFiltros = () => {
    setTab("todas");
    setDepartamentos(TODOS_DEPARTAMENTOS);
    setEstados(TODOS_ESTADOS);
    setTipos(TODAS_CATEGORIAS);
    setCiudades(TODAS_CIUDADES);
    if (esAdministracion) setUsuariosFiltro(TODOS_USUARIOS);
    setRangoFecha("todas");
    setRangoPersonalizado([null, null]);
  };

  const filtrosActivos =
    tab !== "todas" ||
    departamentos.length !== TODOS_DEPARTAMENTOS.length ||
    estados.length !== TODOS_ESTADOS.length ||
    tipos.length !== TODAS_CATEGORIAS.length ||
    ciudades.length !== TODAS_CIUDADES.length ||
    (esAdministracion && usuariosFiltro != null && usuariosFiltro.length !== TODOS_USUARIOS.length) ||
    rangoFecha !== "todas";

  const renderContenido = () => {
    if (loading && items.length === 0) {
      return (
        <div className="nt-card">
          <div className="nt-card-body">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </div>
      );
    }

    if (errorMsg && items.length === 0) {
      return (
        <div className="nt-card">
          <div className="nt-card-body">
            <Result
              status="error"
              title="No se pudieron cargar las notificaciones"
              subTitle={errorMsg}
              extra={
                <Button onClick={() => dispatch(actionNotificacionesGet())}>Reintentar</Button>
              }
            />
          </div>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="nt-card">
          <div className="nt-card-body">
            <Empty description="No hay notificaciones por mostrar" />
          </div>
        </div>
      );
    }

    if (filtrados.length === 0) {
      return (
        <div className="nt-card">
          <div className="nt-card-body">
            <Empty description="Ninguna notificación coincide con los filtros">
              <Button onClick={quitarFiltros}>Quitar filtros</Button>
            </Empty>
          </div>
        </div>
      );
    }

    return (
      <>
        {grupos.map((grupo) => (
          <section key={grupo.etiqueta} className="nt-section" aria-label={grupo.etiqueta}>
            <Text className="nt-group-label">{grupo.etiqueta}</Text>
            <div className="nt-card">
              {grupo.entradas.map((entrada, idx) => (
                <div
                  key={entrada.key}
                  style={{ borderTop: idx > 0 ? `1px solid ${token.colorSplit}` : "none" }}
                >
                  {entrada.tipo === "rafaga" ? (
                    <NotificacionRafaga
                      items={entrada.items}
                      onAbrir={abrirNotificacion}
                      onExpandir={expandirRafaga}
                    />
                  ) : (
                    <NotificacionSimple item={entrada.items[0]} onAbrir={abrirNotificacion} />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

        {entradas.length > visibleCount && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Button onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}>
              Cargar notificaciones anteriores
            </Button>
          </div>
        )}
      </>
    );
  };

  const TABS = [
    { key: "todas", label: "Todas", count: counts.todas },
    { key: "no_leidas", label: "No leídas", count: counts.no_leidas },
    { key: "leidas", label: "Leídas", count: counts.leidas },
    { key: "urgentes", label: "Urgentes", count: counts.urgentes, urgente: true },
  ];

  return (
    <main className="nt-main">
      <div className="nt-content">
        <section className="nt-section">
          <div className="nt-card">
            <div className="nt-card-body">
              <div className="nt-header-row">
                <div>
                  <Title level={3} className="nt-title">
                    Notificaciones
                  </Title>
                  <Text className="nt-subtitle">
                    Mostrando {notificacionesMostradas} de {filtrados.length}
                  </Text>
                </div>
              </div>

              <div className="nt-tabs" role="tablist" aria-label="Filtrar por estado de lectura">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    className={`nt-tab-btn ${tab === t.key ? "nt-tab-btn-active" : ""}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                    <span className={`nt-tab-count ${t.urgente && t.count ? "nt-tab-count--urgente" : ""}`}>
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="nt-section">
          <div className="nt-card">
            <div className="nt-card-body">
              <div className="nt-filtros-row">
                <div className="nt-filtros">
                  <Text type="secondary" style={{ marginRight: 4 }}>
                    Filtrar por
                  </Text>
                  <FiltroChip
                    titulo="Módulo"
                    valor={departamentos}
                    onChange={setDepartamentos}
                    opciones={TODOS_DEPARTAMENTOS.map((k) => ({ value: k, label: DEPARTAMENTOS[k].label }))}
                  />
                  <FiltroChip
                    titulo="Estado"
                    valor={estados}
                    onChange={setEstados}
                    opciones={[
                      ...TODOS_ESTADOS.filter((k) => k !== "sin_estado").map((k) => ({
                        value: k,
                        label: ESTADOS[k].label,
                      })),
                      { value: "sin_estado", label: "Sin estado / otro" },
                    ]}
                  />
                  <FiltroChip
                    titulo="Tipo"
                    valor={tipos}
                    onChange={setTipos}
                    opciones={TODAS_CATEGORIAS.map((k) => ({ value: k, label: CATEGORIAS[k] }))}
                  />
                  <FiltroChip
                    titulo="Ciudad"
                    valor={ciudades}
                    onChange={setCiudades}
                    opciones={TODAS_CIUDADES.map((k) => ({ value: k, label: CITY_NAMES[k] }))}
                  />
                  {esAdministracion && (
                    <FiltroChip
                      titulo="Usuario"
                      valor={usuariosFiltro || TODOS_USUARIOS}
                      onChange={setUsuariosFiltro}
                      opciones={listaUsuarios.map((u) => ({ value: String(u.id_user), label: u.name }))}
                    />
                  )}
                  {filtrosActivos && (
                    <Button type="link" onClick={quitarFiltros}>
                      Quitar filtros
                    </Button>
                  )}
                </div>

                <Button
                  icon={<CheckOutlined aria-hidden="true" />}
                  onClick={confirmarMarcarTodas}
                  disabled={noLeidasFiltradas.length === 0}
                  style={{ height: 44, paddingInline: 16, borderRadius: 10, flex: "0 0 auto" }}
                >
                  Marcar todas como leídas
                </Button>
              </div>

              <div className="nt-filtros-divider" />

              <div className="nt-fecha-row">
                <Text type="secondary" style={{ marginRight: 4 }}>
                  Fecha
                </Text>
                {RANGOS_FECHA.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    className={`nt-fecha-btn ${rangoFecha === r.key ? "nt-fecha-btn-active" : ""}`}
                    onClick={() => setRangoFecha(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
                {rangoFecha === "personalizado" && (
                  <RangePicker
                    value={rangoPersonalizado}
                    onChange={(valores) => setRangoPersonalizado(valores || [null, null])}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    style={{ height: 36 }}
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {renderContenido()}
      </div>
    </main>
  );
}
